import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { euro } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, TrendingUp, PieChart, Users, FileDown, ArrowRight, AlertTriangle, CalendarClock, X, ArrowUpRight, ArrowDownRight, UserCog, MessagesSquare } from "lucide-react";
import OneToOneModal from "@/components/OneToOneModal";
import { BannerDaChiudere } from "@/components/FinanzaChiusura";

const fmtDataIt = (s) => { if (!s) return ""; const [y, m, d] = String(s).slice(0, 10).split("-"); return (d && m && y) ? `${d}/${m}/${y}` : s; };

const mesePrecLabel = (m) => {
  try { const [y, mm] = m.split("-").map(Number); return new Date(y, mm - 1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" }); }
  catch { return m; }
};

export default function CabinaRegia({ onVistaOperativa }) {
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const [costi, setCosti] = useState(null);
  const [marketing, setMarketing] = useState(null);
  const [margini, setMargini] = useState(null);
  const [costoAgenti, setCostoAgenti] = useState(null);
  const [agentiO2o, setAgentiO2o] = useState([]);
  const [o2o, setO2o] = useState("");
  const [voci, setVoci] = useState(null);
  const [scaricando, setScaricando] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [loadingDett, setLoadingDett] = useState(false);
  const refs = { cassa: useRef(null), provvigioni: useRef(null), costi: useRef(null), team: useRef(null) };

  useEffect(() => {
    api.get("/direzione/cabina").then((r) => setC(r.data)).catch(() => {});
    api.get("/team/one-to-one-agenti").then((r) => setAgentiO2o(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get(`/direzione/costi?mese=${mese}`).then((r) => setCosti(r.data)).catch(() => {});
    api.get(`/direzione/marketing-canale?mese=${mese}`).then((r) => setMarketing(r.data)).catch(() => {});
    api.get(`/direzione/margini-team?mese=${mese}`).then((r) => setMargini(r.data)).catch(() => {});
    api.get(`/direzione/costo-agenti?mese=${mese}`).then((r) => setCostoAgenti(r.data)).catch(() => {});
  }, [mese]);

  const scrollTo = (k) => refs[k].current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const apriDettaglio = async () => {
    setLoadingDett(true);
    try {
      const r = await api.get("/direzione/cassa/previsione");
      setDettaglio(r.data);
    } catch { toast.error("Errore nel caricare il dettaglio"); } finally { setLoadingDett(false); }
  };

  const scaricaPdf = async () => {
    setScaricando(true);
    try {
      const res = await api.get(`/direzione/report-mensile/pdf?mese=${mese}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `pacchetto_soci_${mese}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Errore nel generare il PDF"); } finally { setScaricando(false); }
  };

  if (!c) return <div className="space-y-4"><div className="skeleton h-24 rounded-2xl" /><div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="skeleton h-28 rounded-2xl" />)}</div></div>;

  const df = c.da_fare;
  const dfParts = [
    df.approvazioni > 0 && `${df.approvazioni} ${df.approvazioni === 1 ? "approvazione" : "approvazioni"}`,
    df.preliminari_senza_incasso > 0 && `${df.preliminari_senza_incasso} ${df.preliminari_senza_incasso === 1 ? "preliminare" : "preliminari"} senza provvigione incassata`,
    df.prossima_scadenza && df.prossima_scadenza.giorni != null && `${df.prossima_scadenza.descrizione} tra ${df.prossima_scadenza.giorni}gg`,
  ].filter(Boolean);

  const QUADRI = [
    { k: "cassa", label: "Cassa", icon: Wallet, val: c.cassa.saldo_impostato ? euro(c.cassa.oggi) : "Saldo iniziale mancante – inseriscilo in Finanza", sub: c.cassa.saldo_impostato ? `90gg: ${euro(c.cassa.previsione_90)}` : "", danger: c.cassa.saldo_impostato && c.cassa.sotto_soglia },
    { k: "provvigioni", label: "Provvigioni", icon: TrendingUp, val: euro(c.provvigioni.incassate), sub: `da incassare ${euro(c.provvigioni.da_incassare)}` },
    { k: "costi", label: "Costi", icon: PieChart, val: euro(costi?.totale || 0), sub: `${(costi?.voci || []).length} categorie` },
    { k: "team", label: "Team", icon: Users, val: euro(c.portafoglio.valore_atteso), sub: `${c.portafoglio.incarichi_attivi} incarichi attivi${c.portafoglio.senza_provvigione > 0 ? ` · ${c.portafoglio.senza_provvigione} senza %` : ""}` },
  ];

  return (
    <div className="fade-up" data-testid="cabina-regia">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--muted)] mb-1">Cabina di regia</div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight">Buongiorno, {c.nome} 👋</h1>
        </div>
        <button data-testid="btn-vista-operativa" onClick={onVistaOperativa}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-semibold hover:bg-slate-100">
          Vista operativa <ArrowRight size={15} />
        </button>
      </div>

      {/* Riepilogo */}
      <BannerDaChiudere mesi={c.mesi_da_chiudere} testid="cabina-da-chiudere" />
      <div className="card-immo p-5 mb-4" data-testid="cabina-riepilogo">
        <p className="text-sm leading-relaxed">
          <b>Immobilart oggi:</b> Cassa (banca) <b>{c.cassa.saldo_impostato ? euro(c.cassa.oggi) : "Saldo iniziale mancante – inseriscilo in Finanza"}</b>{c.cassa.saldo_impostato && <> · tra 90 giorni prevista <b className={c.cassa.sotto_soglia ? "text-red-600" : ""}>{euro(c.cassa.previsione_90)}</b></>}
          {" · "}Provvigioni del mese: <b>{euro(c.provvigioni.maturate)}</b> (incassate <b>{euro(c.provvigioni.incassate)}</b>, in arrivo <b>{euro(c.provvigioni.da_incassare)}</b>)
          {" · "}Portafoglio: <b>{c.portafoglio.incarichi_attivi}</b> incarichi attivi, valore atteso <b>{euro(c.portafoglio.valore_atteso)}</b>
          {c.portafoglio.senza_provvigione > 0 ? <> (<b>{c.portafoglio.senza_provvigione}</b> senza provvigione indicata)</> : null}.
          {c.pareggio?.testo && <span className="block mt-1" data-testid="cabina-pareggio">{c.pareggio.testo}</span>}
        </p>
        {dfParts.length > 0 && (
          <div data-testid="cabina-dafare" className="mt-3 flex items-center gap-2 text-sm text-[#9A6A00] bg-[#FFB020]/10 border border-[#FFB020]/30 rounded-xl px-3 py-2">
            <AlertTriangle size={15} className="shrink-0" /> <span><b>Da fare:</b> {dfParts.join(" • ")}</span>
          </div>
        )}
        {c.automazioni_oggi && (
          <div data-testid="cabina-automazioni" className="mt-2 text-[11px] text-[var(--muted)]">
            {c.automazioni_oggi.eseguite
              ? <>Automazioni di oggi: eseguite alle {c.automazioni_oggi.ora || "—"} ✅</>
              : <span className="text-[#9A6A00]">⚠️ Automazioni di oggi: non ancora eseguite</span>}
          </div>
        )}
      </div>

      {/* 4 riquadri cliccabili */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {QUADRI.map((q) => (
          <button key={q.k} data-testid={`cabina-quadro-${q.k}`} onClick={() => scrollTo(q.k)}
            className={`card-immo p-4 text-left hover:-translate-y-0.5 transition-transform ${q.danger ? "border-red-300 bg-red-50" : ""}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${q.danger ? "bg-red-400/15 text-red-500" : "bg-[#FC82ED]/15 text-[#FC82ED]"}`}><q.icon size={18} /></div>
            <div className="text-xs text-[var(--muted)] uppercase tracking-wide">{q.label}</div>
            <div className="mono font-bold text-lg">{q.val}</div>
            <div className="text-[11px] text-[var(--muted)]">{q.sub}</div>
          </button>
        ))}
      </div>

      {/* Selettore mese + PDF */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <label className="text-sm flex items-center gap-2">Mese di analisi:
          <input data-testid="cabina-mese" type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm" />
        </label>
        <button data-testid="cabina-pdf" onClick={scaricaPdf} disabled={scaricando}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#111111] text-white text-sm font-semibold disabled:opacity-60">
          <FileDown size={15} /> {scaricando ? "Preparo…" : "Pacchetto riunione soci (PDF)"}
        </button>
      </div>

      {/* Cassa */}
      <div ref={refs.cassa} className="card-immo p-5 mb-6 scroll-mt-20" data-testid="cabina-cassa">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-3"><Wallet size={14} /> Cassa e previsione 90 giorni</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Kpi label="Cassa oggi (banca)" val={c.cassa.saldo_impostato ? euro(c.cassa.oggi) : "Saldo iniziale mancante – inseriscilo in Finanza"} sub={c.cassa.saldo_impostato ? `aggiornata al ${(c.cassa.aggiornata_al || "").split("-").reverse().join("/")}` : "inserisci il saldo in Finanza → Impostazioni"} />
          <Kpi label="Prevista tra 90 giorni" val={c.cassa.saldo_impostato ? euro(c.cassa.previsione_90) : "Saldo iniziale mancante – inseriscilo in Finanza"} sub={c.cassa.saldo_impostato ? (c.cassa.dettaglio?.senza_vendite ? "Senza nuove vendite" : "con le vendite attese") : ""} danger={c.cassa.saldo_impostato && c.cassa.sotto_soglia} />
          <Kpi label="Minimo previsto" val={c.cassa.saldo_impostato ? euro(c.cassa.min_saldo) : "Saldo iniziale mancante – inseriscilo in Finanza"} sub={c.cassa.saldo_impostato ? c.cassa.min_data : ""} danger={c.cassa.saldo_impostato && c.cassa.sotto_soglia} />
        </div>
        {c.cassa.dettaglio && c.cassa.saldo_impostato && <DettaglioPrev d={c.cassa.dettaglio} />}
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <button data-testid="cabina-cassa-dettaglio" onClick={apriDettaglio} disabled={loadingDett} className="text-sm font-semibold text-[#FC82ED] hover:underline inline-flex items-center gap-1 disabled:opacity-60">{loadingDett ? "Carico…" : "Vedi dettaglio"} <ArrowRight size={14} /></button>
          <button onClick={() => nav("/finanza")} className="text-sm font-semibold text-[var(--muted)] hover:underline inline-flex items-center gap-1">Saldo banca in Finanza → Impostazioni <ArrowRight size={14} /></button>
        </div>
      </div>

      {dettaglio && <CassaDettaglioModal data={dettaglio} onClose={() => setDettaglio(null)} />}

      {/* Provvigioni */}
      <div ref={refs.provvigioni} className="card-immo p-5 mb-6 scroll-mt-20" data-testid="cabina-provvigioni">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-3"><TrendingUp size={14} /> Provvigioni dell'agenzia ({mesePrecLabel(c.mese)})</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Kpi label="Totale del mese" val={euro(c.provvigioni.maturate)} />
          <Kpi label="Incassate" val={euro(c.provvigioni.incassate)} />
          <Kpi label="In arrivo (previste nel mese)" val={euro(c.provvigioni.da_incassare)} />
        </div>
        <button onClick={() => nav("/finanza")} className="text-sm font-semibold text-[#FC82ED] hover:underline mt-3 inline-flex items-center gap-1">Apri Finanza Aziendale <ArrowRight size={14} /></button>
      </div>

      {/* Costi per categoria */}
      <div ref={refs.costi} className="card-immo p-5 mb-6 scroll-mt-20" data-testid="cabina-costi">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-3"><PieChart size={14} /> Costi del mese (righe banca)</div>
        {costi && <div data-testid="cabina-costi-previsto" className="text-sm mb-2">Reali {euro(costi.totale)} <span className="text-[var(--muted)]">· previsto {euro(costi.previsto)} (fisse {euro(costi.previsto_fisse)} + variabili medie {euro(costi.previsto_variabili)})</span></div>}
        {!costi || costi.voci.length === 0 ? <Empty>Nessuna riga banca di costo in {mesePrecLabel(mese)}.</Empty> : (
          <div className="space-y-2">
            {costi.voci.map((v) => (
              <div key={v.categoria} data-testid={`cabina-costo-${v.categoria}`} className="flex items-center justify-between text-sm py-1.5 border-b border-[var(--border)] last:border-0">
                <span>{v.categoria}</span><span className="mono font-semibold">{euro(v.importo)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between text-sm pt-2 font-bold"><span>Totale</span><span className="mono">{euro(costi.totale)}</span></div>
          </div>
        )}
      </div>

      {/* Marketing per canale */}
      <div className="card-immo p-5 mb-6" data-testid="cabina-marketing">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-1"><PieChart size={14} /> Marketing per canale</div>
        <p className="text-[11px] text-[var(--muted)] mb-3">Spesa per canale collegata alla provenienza delle notizie. Tagga una spesa di categoria "Marketing" con il canale in Finanza per vederla qui.</p>
        {!marketing || marketing.canali.length === 0 ? <Empty>Nessun dato marketing per {mesePrecLabel(mese)}.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase text-[var(--muted)]">
                <th className="py-1.5">Canale</th><th className="text-right">Spesa</th><th className="text-right">Notizie</th><th className="text-right">Incarichi</th><th className="text-right">€/incarico</th>
              </tr></thead>
              <tbody>
                {marketing.canali.map((m) => (
                  <tr key={m.canale} data-testid={`cabina-canale-${m.canale}`} className="border-t border-[var(--border)]">
                    <td className="py-2">{m.canale}</td>
                    <td className="text-right mono">{euro(m.spesa)}</td>
                    <td className="text-right mono">{m.notizie}</td>
                    <td className="text-right mono">{m.incarichi}</td>
                    <td className="text-right mono">{m.costo_per_incarico != null ? euro(m.costo_per_incarico) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Margine per team */}
      <div ref={refs.team} className="card-immo p-5 mb-6 scroll-mt-20" data-testid="cabina-team">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-1"><Users size={14} /> Margine per team</div>
        <p className="text-[11px] text-[var(--muted)] mb-3">Entrate e quote dalle righe banca (attribuite al team tramite l'abbinamento CRM) − quota dei costi banca del mese.</p>
        {!margini || margini.team.length === 0 ? <Empty>Nessun incasso per team in {mesePrecLabel(mese)}.</Empty> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase text-[var(--muted)]">
                <th className="py-1.5">Team</th><th className="text-right">Incassato</th><th className="text-right">Collab.</th><th className="text-right">Costi</th><th className="text-right">Margine netto</th>
              </tr></thead>
              <tbody>
                {margini.team.map((t) => (
                  <tr key={t.team} data-testid={`cabina-team-${t.team}`} className="border-t border-[var(--border)]">
                    <td className="py-2 font-medium">{t.team}</td>
                    <td className="text-right mono">{euro(t.incassato)}</td>
                    <td className="text-right mono">{euro(t.pagato_collaboratori)}</td>
                    <td className="text-right mono">{euro(t.costi_allocati)}</td>
                    <td className={`text-right mono font-bold ${t.margine_netto < 0 ? "text-red-600" : "text-emerald-600"}`}>{euro(t.margine_netto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CostoAgenti data={costoAgenti} mese={mese} onVoci={setVoci} />

      <div className="card-immo p-5 mb-6" data-testid="cabina-o2o">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-3"><MessagesSquare size={14} /> One-to-one agenti</div>
        <div className="flex gap-2 flex-wrap">
          <select data-testid="cabina-o2o-select" value={o2o} onChange={(e) => setO2o(e.target.value)} className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-[var(--border)] text-sm">
            <option value="">Scegli un agente…</option>
            {agentiO2o.map((a) => <option key={a.id} value={a.id}>{a.nome} · {a.team}</option>)}
          </select>
          <button data-testid="cabina-o2o-open" disabled={!o2o} onClick={() => setO2o(o2o + "#open")} className="px-4 py-2 rounded-lg bg-[#111111] text-white text-sm font-semibold disabled:opacity-40">Apri one-to-one</button>
        </div>
      </div>
      {o2o.endsWith("#open") && <OneToOneModal agentId={o2o.replace("#open", "")} onClose={() => setO2o(o2o.replace("#open", ""))} />}
      {voci && <VociModal r={voci} onClose={() => setVoci(null)} />}
    </div>
  );
}

function DettaglioPrev({ d }) {
  const righe = [["Cassa oggi", d.cassa_oggi], ["+ Provvigioni in arrivo (sicure + probabili)", d.provvigioni], ["− Quote collaboratori", d.quote_collaboratori],
    ["+ Entrate previste manuali", d.entrate_previste], ["− Spese fisse previste", d.spese_fisse], ["− Spese variabili previste manuali", d.spese_variabili],
    ["− Scadenze fiscali", d.fiscali]];
  return (
    <div className="mt-3 rounded-xl border border-[var(--border)] p-3 text-sm max-w-xl" data-testid="cabina-previsione-calcolo">
      {righe.map(([l, v]) => <div key={l} className="flex justify-between gap-3 py-0.5"><span className="text-[var(--muted)]">{l}</span><b className="mono">{euro(v)}</b></div>)}
      <div className="flex justify-between gap-3 pt-1 mt-1 border-t border-slate-100"><span className="font-semibold">= Previsione 90 giorni</span><b className="mono">{euro(d.previsione_90)}</b></div>
      {d.senza_vendite && <div data-testid="cabina-senza-vendite" className="text-xs text-amber-700 mt-1">Senza nuove vendite: nessuna provvigione in arrivo nei 90 giorni.</div>}
    </div>
  );
}


function Kpi({ label, val, sub, danger }) {
  return (
    <div className={`rounded-xl p-3 border ${danger ? "border-red-200 bg-red-50" : "border-[var(--border)] bg-[#F7F7F8]"}`}>
      <div className="text-[11px] text-[var(--muted)] uppercase tracking-wide">{label}</div>
      <div className={`mono font-bold text-lg ${danger ? "text-red-600" : ""}`}>{val}</div>
      {sub && <div className="text-[11px] text-[var(--muted)]">{sub}</div>}
    </div>
  );
}

function CostoAgenti({ data, mese, onVoci }) {
  return (
    <div className="card-immo p-5 mb-6" data-testid="cabina-costo-agenti">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-1"><UserCog size={14} /> Costo per agente</div>
      <p className="text-[11px] text-[var(--muted)] mb-3">Provvigioni incassate dall'agenzia − provvigioni pagate all'agente − costi diretti collegati all'agente (in Finanza scegli "Agente" sulla spesa).</p>
      {!data || data.agenti.length === 0 ? <Empty>Nessun dato per agente in {mesePrecLabel(mese)}.</Empty> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left text-[11px] uppercase text-[var(--muted)]">
              <th className="py-1.5">Agente</th><th className="text-right">Incassato</th><th className="text-right">Provv. generate</th><th className="text-right">Provv. pagate</th><th className="text-right">Costi diretti</th><th className="text-right">Margine</th>
            </tr></thead>
            <tbody>
              {data.agenti.map((a) => (
                <tr key={a.agente_id} data-testid={`cabina-agente-${a.agente_id}`} className="border-t border-[var(--border)]">
                  <td className="py-2"><div className="font-medium">{a.agente}</div><div className="text-[11px] text-[var(--muted)]">{a.team}</div></td>
                  <td className="text-right mono">{euro(a.incassato)}</td>
                  <td className="text-right mono">{euro(a.provv_generate)}</td>
                  <td className="text-right mono">{euro(a.provv_pagate)}</td>
                  <td className="text-right mono">{a.voci_costi.length ? <button data-testid={`cabina-agente-costi-${a.agente_id}`} onClick={() => onVoci(a)} className="underline underline-offset-2 hover:text-[#b84fae]">{euro(a.costi_diretti)}</button> : euro(a.costi_diretti)}</td>
                  <td className={`text-right mono font-bold ${a.margine < 0 ? "text-red-600" : "text-emerald-600"}`}>{euro(a.margine)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function VociModal({ r, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="costi-agente-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md card-immo p-5">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold">Costi diretti · {r.agente}</h3><button onClick={onClose} data-testid="costi-agente-close" className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button></div>
        {r.voci_costi.map((v, i) => (
          <div key={i} className="flex justify-between gap-3 text-sm py-1.5 border-b border-[var(--border)] last:border-0"><span className="truncate">{v.descrizione} <span className="text-[11px] text-[var(--muted)]">· {v.categoria} · {fmtDataIt(v.data)}</span></span><b className="mono">{euro(v.importo)}</b></div>
        ))}
      </div>
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-sm text-[var(--muted)] py-3 text-center">{children}</div>;
}


function CassaDettaglioModal({ data, onClose }) {
  const eventi = data.eventi || [];
  const entrate = eventi.filter((e) => e.importo > 0);
  const uscite = eventi.filter((e) => e.importo < 0);
  const totEntrate = entrate.reduce((s, e) => s + e.importo, 0);
  const totUscite = uscite.reduce((s, e) => s + e.importo, 0);
  const Riga = ({ e }) => (
    <div data-testid="cassa-dett-riga" className="flex items-center justify-between gap-2 text-sm border-b border-[var(--border)] last:border-0 py-1.5">
      <div className="flex items-center gap-2 min-w-0">
        {e.importo > 0 ? <ArrowUpRight size={14} className="text-emerald-600 shrink-0" /> : <ArrowDownRight size={14} className="text-red-500 shrink-0" />}
        <span className="truncate">{e.causale || "—"}</span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-[11px] text-[var(--muted)]">{fmtDataIt(e.data)}</span>
        <span className={`mono font-semibold ${e.importo > 0 ? "text-emerald-600" : "text-red-600"}`}>{euro(e.importo)}</span>
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="cassa-dettaglio-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="font-semibold">Dettaglio previsione cassa — 90 giorni</div>
          <button data-testid="cassa-dettaglio-close" onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X size={16} /></button>
        </div>
        <p className="text-[11px] text-[var(--muted)] mb-4">{data.saldo_impostato === false ? "Saldo iniziale mancante – inseriscilo in Finanza." : <>Saldo oggi {euro(data.saldo_attuale)} → previsto {euro(data.saldo_fine)} tra 90 giorni.</>} Ogni voce cade nella data indicata.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Entrate previste · {euro(totEntrate)}</div>
            {entrate.length === 0 ? <Empty>Nessuna entrata prevista.</Empty> : entrate.map((e, i) => <Riga key={i} e={e} />)}
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-red-700 mb-2">Uscite previste · {euro(totUscite)}</div>
            {uscite.length === 0 ? <Empty>Nessuna uscita prevista.</Empty> : uscite.map((e, i) => <Riga key={i} e={e} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
